import React, { useEffect, useState } from 'react';
import { adminService } from '../../services/admin.service';
import StatCard from '../../components/cards/StatCard';
import Badge from '../../components/common/Badge';
import { useNavigate } from 'react-router-dom';

const AdminDashboard: React.FC = () => {
  const [users, setUsers] = useState<any[]>([]);
  const [pendingKyc, setPendingKyc] = useState<any[]>([]);
  const [pendingWithdrawals, setPendingWithdrawals] = useState<any[]>([]);
  const [btcAddress, setBtcAddress] = useState('');
  const [tempBtcAddress, setTempBtcAddress] = useState('');
  const [isEditingBtc, setIsEditingBtc] = useState(false);
  const [btcUpdating, setBtcUpdating] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  const fetchData = async () => {
    setError(null);
    try {
      const [userRes, btcRes, kycRes, withdrawRes] = await Promise.all([
        adminService.getUsers(),
        adminService.getBtcAddress(),
        adminService.getKYCPending(),
        adminService.getWithdrawals()
      ]);
      
      const allUsers = Array.isArray(userRes) ? userRes : (userRes?.data || []);
      setUsers(allUsers);
      
      // Fixed data path to btcAddress as per API output
      const addr = btcRes?.data?.btcAddress || btcRes?.data?.address || btcRes?.btcAddress || '';
      setBtcAddress(addr);
      setTempBtcAddress(addr);
      
      const kycList = Array.isArray(kycRes) ? kycRes : (kycRes?.data || []);
      setPendingKyc(kycList);

      const withdrawList = Array.isArray(withdrawRes) ? withdrawRes : (withdrawRes?.data || []);
      setPendingWithdrawals(withdrawList.filter((w: any) => w.status === 'PENDING'));
    } catch (err: any) {
      setError(err.message || 'Node synchronization failed.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleUpdateBtc = async () => {
    if (!tempBtcAddress) return;
    setBtcUpdating(true);
    try {
      await adminService.updateBtcAddress(tempBtcAddress);
      setBtcAddress(tempBtcAddress);
      setIsEditingBtc(false);
      alert("BTC Broadcast Node Updated Successfully");
    } catch (err) {
      alert("BTC Update Failed");
    } finally {
      setBtcUpdating(false);
    }
  };

  const handleCancelEdit = () => {
    setTempBtcAddress(btcAddress);
    setIsEditingBtc(false);
  };

  if (loading) return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 animate-pulse p-10">
      {[1, 2, 3, 4].map(i => <div key={i} className="h-48 bg-slate-900/50 rounded-[2.5rem] border border-white/5"></div>)}
    </div>
  );

  const stats = {
    total: users.length,
    verified: users.filter(u => u.verified === 1 || u.verified === true).length,
    active: users.filter(u => u.activeStatus == 1 || u.activeStatus === true).length,
    suspended: users.filter(u => u.activeStatus == 0 || u.activeStatus === false).length
  };

  return (
    <div className="space-y-10 animate-in fade-in slide-in-from-bottom-6 duration-700">
      {/* Primary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard label="Total Population" value={stats.total} color="purple" icon={<path d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />} />
        <StatCard label="Identity Cleared" value={stats.verified} color="green" icon={<path d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />} />
        <StatCard label="Active Nodes" value={stats.active} color="blue" icon={<path d="M13 10V3L4 14h7v7l9-11h-7z" />} />
        <StatCard label="Suspended" value={stats.suspended} color="red" icon={<path d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 00-2 2zm10-10V7a4 4 0 00-8 0v4h8z" />} />
      </div>

      {/* BTC BROADCAST NODE WIDGET - Enhanced with Toggle Editing */}
      <div className="bg-[#030614] border border-white/5 rounded-[3rem] p-10 lg:p-12 shadow-2xl relative overflow-hidden">
        <div className="flex items-center justify-between mb-10">
          <div>
            <h3 className="text-2xl font-black text-white uppercase tracking-tight">Main BTC Broadcast Node</h3>
            <p className="text-slate-500 text-[10px] font-black uppercase tracking-[0.3em] mt-2">Configure Global Deposit Identity</p>
          </div>
          <div className="w-14 h-14 bg-purple-900/10 rounded-2xl flex items-center justify-center border border-purple-500/20">
             <svg className="w-7 h-7 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>
          </div>
        </div>

        <div className="relative group mb-10">
          {isEditingBtc ? (
            <div className="flex flex-col md:flex-row items-center space-y-4 md:space-y-0 md:space-x-4 animate-in fade-in zoom-in-95 duration-200">
              <input 
                type="text" 
                autoFocus
                value={tempBtcAddress}
                onChange={e => setTempBtcAddress(e.target.value)}
                className="w-full md:flex-1 bg-[#05071a] border border-purple-500/40 rounded-3xl px-8 py-6 text-white font-mono text-sm outline-none focus:ring-4 focus:ring-purple-600/10 transition-all shadow-inner"
                placeholder="bc1q..."
              />
              <div className="flex space-x-3 w-full md:w-auto">
                <button 
                  onClick={handleCancelEdit}
                  className="flex-1 md:flex-none px-8 py-4 bg-slate-900 text-slate-500 hover:text-white font-black text-[11px] uppercase rounded-2xl transition-all border border-white/5"
                >
                  Cancel
                </button>
                <button 
                  onClick={handleUpdateBtc}
                  disabled={btcUpdating || !tempBtcAddress}
                  className="flex-1 md:flex-none px-10 py-4 bg-purple-600 text-white font-black text-[11px] uppercase rounded-2xl transition-all shadow-xl shadow-purple-600/30 hover:bg-purple-500 disabled:opacity-30"
                >
                  {btcUpdating ? 'Saving...' : 'Update'}
                </button>
              </div>
            </div>
          ) : (
            <div 
              onClick={() => setIsEditingBtc(true)}
              className="w-full bg-[#05071a]/50 border border-slate-800/60 rounded-3xl px-8 py-6 flex items-center justify-between cursor-pointer group hover:border-purple-500/30 transition-all backdrop-blur-sm"
            >
              <span className="text-white font-mono text-sm tracking-widest truncate mr-10">
                {btcAddress || 'No Address Configured'}
              </span>
              <div className="flex items-center space-x-3 text-slate-600 group-hover:text-purple-400 transition-colors">
                 <span className="text-[10px] font-black uppercase tracking-widest hidden sm:block">Modify Node</span>
                 <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" /></svg>
              </div>
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-[#05071a] p-6 rounded-3xl border border-white/5">
            <p className="text-[9px] font-black text-slate-600 uppercase mb-2 tracking-widest">Last Update</p>
            <p className="text-xs text-white font-bold">Today, 09:12 AM</p>
          </div>
          <div className="bg-[#05071a] p-6 rounded-3xl border border-white/5">
            <p className="text-[9px] font-black text-slate-600 uppercase mb-2 tracking-widest">Network</p>
            <p className="text-xs text-purple-400 font-black uppercase">Bitcoin Mainnet</p>
          </div>
          <div className="bg-[#05071a] p-6 rounded-3xl border border-white/5">
            <p className="text-[9px] font-black text-slate-600 uppercase mb-2 tracking-widest">Integrity</p>
            <p className="text-xs text-emerald-500 font-black uppercase tracking-widest">Secured</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* KYC AUDIT QUEUE - Classic High Fidelity Design */}
        <div className="bg-[#030614] border border-white/5 rounded-[3rem] p-10 lg:p-12 shadow-2xl relative overflow-hidden">
          <div className="flex items-center justify-between mb-10">
             <div className="flex items-center space-x-3">
               <div className="w-4 h-4 bg-amber-600/60 rounded-full"></div>
               <h3 className="text-lg font-black text-white uppercase tracking-tight">KYC Audit Queue</h3>
             </div>
             <button onClick={() => navigate('/admin/kyc')} className="text-[10px] font-black text-slate-500 hover:text-white transition-colors uppercase tracking-widest">View All</button>
          </div>
          
          <div className="space-y-4">
             {pendingKyc.length > 0 ? pendingKyc.slice(0, 4).map((u) => (
               <div key={u.userId} className="flex items-center justify-between p-6 bg-[#05071a]/80 rounded-[2rem] border border-white/5 hover:border-amber-500/20 transition-all group">
                  <div className="flex items-center space-x-5">
                     <div className="w-14 h-14 bg-slate-900/80 rounded-2xl flex items-center justify-center font-black text-slate-500 group-hover:text-amber-500 transition-colors uppercase text-xs border border-white/5 shadow-inner">
                        {u.fname?.[0]}{u.lname?.[0]}
                     </div>
                     <div>
                        <p className="text-white font-black text-sm tracking-tight">{u.fname} {u.lname}</p>
                        <p className="text-[10px] text-slate-600 font-mono mt-0.5">{u.email}</p>
                     </div>
                  </div>
                  <button 
                    onClick={() => navigate('/admin/kyc')}
                    className="px-6 py-3 bg-amber-500/5 hover:bg-amber-500 border border-amber-500/20 hover:border-amber-400 text-amber-500 hover:text-slate-950 font-black text-[10px] uppercase tracking-widest rounded-xl transition-all shadow-lg shadow-amber-500/5"
                  >
                    Audit
                  </button>
               </div>
             )) : (
               <div className="py-20 text-center opacity-20">
                  <p className="text-[10px] font-black uppercase tracking-widest">Signal Queue Empty</p>
               </div>
             )}
          </div>
        </div>

        {/* WITHDRAWAL OUTBOX - Classic High Fidelity Design */}
        <div className="bg-[#030614] border border-white/5 rounded-[3rem] p-10 lg:p-12 shadow-2xl relative overflow-hidden">
          <div className="flex items-center justify-between mb-10">
             <div className="flex items-center space-x-3">
               <div className="w-4 h-4 bg-emerald-600/60 rounded-full"></div>
               <h3 className="text-lg font-black text-white uppercase tracking-tight">Withdrawal Outbox</h3>
             </div>
             <button onClick={() => navigate('/admin/withdrawals')} className="text-[10px] font-black text-slate-500 hover:text-white transition-colors uppercase tracking-widest">View All</button>
          </div>
          
          <div className="space-y-4">
             {pendingWithdrawals.length > 0 ? pendingWithdrawals.slice(0, 4).map((w) => (
               <div key={w.id} className="flex items-center justify-between p-6 bg-[#05071a]/80 rounded-[2rem] border border-white/5 hover:border-emerald-500/20 transition-all group">
                  <div className="flex items-center space-x-5">
                     <div className="w-14 h-14 bg-slate-900/80 rounded-2xl flex items-center justify-center font-black text-slate-500 group-hover:text-emerald-500 transition-colors uppercase text-xs border border-white/5 shadow-inner">TX</div>
                     <div>
                        <p className="text-white font-black text-sm tracking-tight">{w.email}</p>
                        <p className="text-[10px] text-emerald-500 font-black mt-0.5 tracking-tighter">${Number(w.amount).toFixed(2)}</p>
                     </div>
                  </div>
                  <button 
                    onClick={() => navigate('/admin/withdrawals')}
                    className="px-6 py-3 bg-emerald-500/5 hover:bg-emerald-500 border border-emerald-500/20 hover:border-emerald-400 text-emerald-500 hover:text-slate-950 font-black text-[10px] uppercase tracking-widest rounded-xl transition-all shadow-lg shadow-emerald-500/5"
                  >
                    Process
                  </button>
               </div>
             )) : (
               <div className="py-20 text-center opacity-20">
                  <p className="text-[10px] font-black uppercase tracking-widest">No pending broadcasts</p>
               </div>
             )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;